import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const CountryComparator = ({ countries = [] }) => {
    const [countryA, setCountryA] = useState(null);
    const [countryB, setCountryB] = useState(null);

    // Initial Defaults (random or specific)
    useEffect(() => {
        if (countries.length >= 2 && !countryA && !countryB) {
            setCountryA(countries[0]);
            setCountryB(countries[1]);
        }
    }, [countries]);

    const handleSelect = (code, setFunc) => {
        const selected = countries.find(c => c.code === code);
        setFunc(selected);
    };

    if (!countryA || !countryB) return null;

    // Helper: Labels
    const getLabel = (key) => key ? key.replace(/_/g, ' ') : 'N/A';

    return (
        <div className="premium-comparator" style={{ marginTop: '6rem', marginBottom: '4rem' }}>
            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
                <h2 style={{ fontSize: '2rem', fontWeight: 700, background: 'linear-gradient(to right, #60a5fa, #a855f7)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', margin: 0 }}>
                    System Comparator
                </h2>
                <p style={{ color: '#94a3b8', marginTop: '0.5rem' }}>Direct side-by-side analysis of structural tradeoffs.</p>
            </div>

            {/* Selectors */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '2rem' }}>
                <select
                    value={countryA.code}
                    onChange={(e) => handleSelect(e.target.value, setCountryA)}
                    className="premium-select"
                >
                    {countries.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                </select>
                <select
                    value={countryB.code}
                    onChange={(e) => handleSelect(e.target.value, setCountryB)}
                    className="premium-select"
                >
                    {countries.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                </select>
            </div>

            <div className="comparator-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', position: 'relative' }}>
                {/* Vertical Divider Line */}
                <div style={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: '1px', background: 'rgba(255,255,255,0.1)', transform: 'translateX(-50%)' }} />

                {/* Country A Column */}
                <ComparisonColumn country={countryA} />

                {/* Country B Column */}
                <ComparisonColumn country={countryB} />
            </div>

            {/* Key Differences Summary */}
            <div style={{ marginTop: '3rem', background: 'rgba(15, 23, 42, 0.4)', borderRadius: '12px', padding: '1.5rem', border: '1px solid rgba(255,255,255,0.05)' }}>
                <h4 style={{ margin: '0 0 1rem 0', color: '#fff', fontSize: '1rem' }}>Key Structural Differences</h4>
                <ul style={{ margin: 0, paddingLeft: '1.2rem', color: '#cbd5e1', lineHeight: '1.6' }}>
                    {countryA.classifiers?.system_type !== countryB.classifiers?.system_type && (
                        <li>
                            <strong>Model:</strong> {countryA.name} uses a <span style={{ color: '#60a5fa' }}>{getLabel(countryA.classifiers?.system_type)}</span> approach, while {countryB.name} is built on <span style={{ color: '#a855f7' }}>{getLabel(countryB.classifiers?.system_type)}</span> logic.
                        </li>
                    )}
                    {countryA.classifiers?.pressure_valve_primary !== countryB.classifiers?.pressure_valve_primary && (
                        <li>
                            <strong>Pressure Valve:</strong> {countryA.name} leans on <span style={{ color: '#eab308' }}>{getLabel(countryA.classifiers?.pressure_valve_primary)}</span> to control costs, whereas {countryB.name} relies on <span style={{ color: '#eab308' }}>{getLabel(countryB.classifiers?.pressure_valve_primary)}</span>.
                        </li>
                    )}
                    <li>
                        <strong>Common DNA:</strong> {
                            calculateCommonTraits(countryA, countryB).length > 0
                                ? `They both share traits like ${calculateCommonTraits(countryA, countryB).map(t => t.replace(/_/g, ' ')).join(', ')}.`
                                : "These systems have very different structural DNA."
                        }
                    </li>
                </ul>
            </div>
        </div>
    );
};

const ComparisonColumn = ({ country }) => {
    if (!country) return null;

    // Find key attributes
    const findAttr = (labelPart) => country.attributes.find(a => a.label.toLowerCase().includes(labelPart.toLowerCase()))?.value || "N/A";

    return (
        <div className="comparator-col">
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                <img src={`/image/flags/${country.code.toLowerCase()}.svg`} alt={country.name} style={{ width: 40, borderRadius: 4 }} />
                <h3 style={{ margin: 0, fontSize: '1.5rem', color: '#fff' }}>{country.name}</h3>
            </div>

            <div className="comp-section">
                <label>System Type</label>
                <div className="comp-chip">{country.classifiers?.system_type?.replace(/_/g, ' ')}</div>
            </div>

            <div className="comp-section">
                <label>Pressure Valve</label>
                <div className="comp-chip warning">{country.classifiers?.pressure_valve_primary?.replace(/_/g, ' ')}</div>
            </div>

            <div className="comp-section">
                <label>The Reality</label>
                <p>{findAttr('Reality')}</p>
            </div>

            <div className="comp-section">
                <label>US Constraints</label>
                <p style={{ color: '#f87171' }}>{findAttr('Constraints')}</p>
            </div>
        </div>
    );
};

function calculateCommonTraits(a, b) {
    if (!a?.classifiers?.traits || !b?.classifiers?.traits) return [];
    return a.classifiers.traits.filter(t => b.classifiers.traits.includes(t));
}

// Add CSS via styled jsx or just inline for now.
// Since we can't add styled-jsx here easily, assuming global CSS or inline styles.
// Added styles inline for 'premium-select' and chips.
const styles = `
.premium-select {
    width: 100%;
    padding: 0.8rem;
    background: #1e293b;
    border: 1px solid #334155;
    border-radius: 8px;
    color: #fff;
    font-size: 1rem;
    cursor: pointer;
}
.comp-section { margin-bottom: 1.5rem; }
.comp-section label { display: block; font-size: 0.75rem; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 0.5rem; }
.comp-section p { font-size: 0.95rem; line-height: 1.5; color: #e2e8f0; margin: 0; }
.comp-chip { display: inline-block; padding: 4px 12px; background: rgba(59, 130, 246, 0.15); border: 1px solid rgba(59, 130, 246, 0.3); color: #60a5fa; border-radius: 20px; font-size: 0.85rem; font-weight: 500; }
.comp-chip.warning { background: rgba(234, 179, 8, 0.15); border-color: rgba(234, 179, 8, 0.3); color: #facc15; }
`;

// Inject styles? Or better yet, append to ShockTherapyArticle.css?
// I'll assume usage of existing CSS or add these classes to ShockTherapyArticle.css later.
export default CountryComparator;
